/**
 * useCatalogUpdates: one live connection to the customer catalogue room.
 * ─────────────────────────────────────────────────────────────────────────────
 * The server broadcasts everything a shopper's screen may need to react to on
 * `catalog:public`: a product or brand switched off, a kitchen unpublished,
 * opening hours changed, a brand outlet switched on or off. Every page that
 * listened used to carry its own copy of the same twenty lines (open a socket,
 * join on connect, register handlers, leave and close on unmount), five copies
 * in all, and the Brands page had none, so a branch switched off on the
 * dashboard stayed on it until the visitor reloaded (4 Oct 2026).
 *
 * Pass a map of event name to handler. Handlers are read through a ref, so
 * they always see the page's latest props and state without the socket being
 * torn down and reopened on every render; the connection is only rebuilt when
 * the SET of events changes or `enabled` flips.
 *
 *   useCatalogUpdates({
 *     outletStatusChanged: (p) => { if (p.parentType === "brand") refetch(); },
 *   });
 *
 * `enabled: false` (for example, no vendor id yet) opens nothing.
 *
 * The socket is a freshness layer only. Every reader on the server still
 * filters by visibility, so a missed event can leave a screen stale but can
 * never show something hidden.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useRef } from "react";
import { io } from "socket.io-client";

// Storefront availability and its checkout slots can both be mounted on one
// page. Share their public connection; each hook owns only its own listeners.
let sharedConnection = null;
const acquireConnection = () => {
  if (!sharedConnection) {
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket", "polling"],
    });
    const connection = { socket, subscribers: new Set() };
    socket.on("connect", () => {
      socket.emit("joinCatalogUpdates");
      connection.subscribers.forEach((onConnect) => onConnect());
    });
    sharedConnection = connection;
  }
  return sharedConnection;
};

export default function useCatalogUpdates(handlers, { enabled = true, onConnect } = {}) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const onConnectRef = useRef(onConnect);
  onConnectRef.current = onConnect;
  const events = Object.keys(handlers || {}).sort().join("|");

  useEffect(() => {
    if (!enabled || !events) return undefined;
    const connection = acquireConnection();
    const { socket } = connection;
    const reconnected = () => onConnectRef.current?.();
    connection.subscribers.add(reconnected);
    if (socket.connected) reconnected();
    const listeners = events.split("|").map((event) => {
      const listener = (payload = {}) => {
        const handle = handlersRef.current?.[event];
        if (typeof handle === "function") handle(payload || {});
      };
      socket.on(event, listener);
      return [event, listener];
    });
    return () => {
      listeners.forEach(([event, listener]) => socket.off(event, listener));
      connection.subscribers.delete(reconnected);
      if (!connection.subscribers.size) {
        socket.emit("leaveCatalogUpdates");
        socket.removeAllListeners();
        socket.close();
        sharedConnection = null;
      }
    };
  }, [enabled, events]);
}
